import { isAfter, isBefore, parseISO } from "date-fns";
import { first, last } from "lodash";
import {
  CheckCheck,
  CircleCheck,
  Code,
  Eye,
  LineChart,
  Mail,
  Radio,
  RefreshCw,
  SquareUserRound,
  TagIcon,
} from "lucide-react";
import { FC, useCallback, useState } from "react";
import { Link } from "react-router";

import { VerificationHeatMap } from "@/components/charts/heatmap";
import { PageViewsChart } from "@/components/charts/page-views-chart";
import { VerificationsChart } from "@/components/charts/verifications-chart";
import { ViewsChart } from "@/components/charts/views-chart";
import { Badge } from "@/components/ui/badge";
import { DataGrid } from "@/components/ui/data-grid";
import { DatePicker } from "@/components/ui/date-picker";
import { Header } from "@/components/ui/header";
import { Metric } from "@/components/ui/metric";
import { useGetSKUAnalyticsQuery } from "@/data/queries";
import {
  SKU,
  TagOwnerChange,
  TagVerification,
  TagVerificationType,
  TagVerificationTypeLabels,
  TransferType,
} from "@/data/types";

type Props = {
  sku: SKU;
};

const getPageViewsByCityCity = (key: string) => first(key.split("|"));

const getPageViewsByCityRegion = (key: string) => {
  const parts = key.split("|");

  if (parts.length === 3) {
    return parts[1];
  }

  return "";
};

const getPageViewsByCityCountry = (key: string) => last(key.split("|"));

const getTagsByViewsID = (key: string) => {
  const uid = first(key.split("|"));

  return (
    <Link to={`/tags/${uid}`} className="hover:underline">
      <TagIcon height={12} className="inline align-baseline" />
      {uid}
    </Link>
  );
};

const getTagsByViewsSerialNumber = (key: string) => last(key.split("|"));

const getTagID = (_: string, { uid }: TagVerification) => (
  <Link to={`/tags/${uid}`} className="hover:underline">
    <TagIcon height={12} className="inline align-baseline" />
    {uid}
  </Link>
);

const getTagIDFromOwnerChange = (_: string, { tag_uid }: TagOwnerChange) => (
  <Link to={`/tags/${tag_uid}`} className="hover:underline">
    <TagIcon height={12} className="inline align-baseline" />
    {tag_uid}
  </Link>
);

const getTagVerificationLocation = (
  _: string,
  { city, region, country }: TagVerification,
) => [city, region, country].filter(Boolean).join(", ");

const getTagVerificationType = (_: string, tag: TagVerification) => {
  switch (tag.verify_type) {
    case TagVerificationType.UserView: {
      return (
        <Badge variant="blue">
          {TagVerificationTypeLabels[TagVerificationType.UserView]}
        </Badge>
      );
    }
    case TagVerificationType.ProgrammingQC: {
      return (
        <Badge variant="purple">
          {TagVerificationTypeLabels[TagVerificationType.ProgrammingQC]}
        </Badge>
      );
    }
    case TagVerificationType.Custom:
    default: {
      const name = tag?.custom_verification_type?.name;
      return (
        <Badge variant="green">
          {name || TagVerificationTypeLabels[TagVerificationType.Custom]}
        </Badge>
      );
    }
  }
};

const getTransferType = (_: string, tagOwnerChange: TagOwnerChange) => {
  switch (tagOwnerChange.transfer_type) {
    case TransferType.claim: {
      return <Badge variant="sky">Claim</Badge>;
    }
    case TransferType.recover:
    default: {
      return <Badge variant="fuchsia">Reset</Badge>;
    }
  }
};

const getTagSerialNumber = (
  _: string,
  { nft_token_id }: TagVerification | TagOwnerChange,
) => nft_token_id;

const getTagVerificationTime = (
  _: string,
  { created_at }: TagVerification | TagOwnerChange,
) => parseISO(created_at).toLocaleString();

const getConsumerUserId = (
  _: string,
  { sender_address, receiver_address }: TagOwnerChange,
) => sender_address || receiver_address;

const getConsumerEmail = (
  _: string,
  { sender_email, receiver_email }: TagOwnerChange,
) => sender_email || receiver_email || "Unknown";

export const AnalyticsTab: FC<Props> = ({ sku }) => {
  const skuCreationDate = parseISO(sku.created_at);
  const todayDate = new Date();

  const [startDate, setStartDate] = useState<Date>(skuCreationDate);
  const [endDate, setEndDate] = useState<Date>(todayDate);

  const [startDateOpen, setStartDateOpen] = useState<boolean>(false);
  const [endDateOpen, setEndDateOpen] = useState<boolean>(false);

  const { data, isLoading } = useGetSKUAnalyticsQuery(
    sku.id.toString(),
    startDate,
    endDate,
  );

  const handleSetStartDate = useCallback(
    (date?: Date) => {
      if (!date) {
        return;
      }

      if (endDate && isAfter(date, endDate)) {
        setEndDate(date);
      }

      setStartDate(date);
      setStartDateOpen(false);
    },
    [endDate, setStartDate, setEndDate, setStartDateOpen],
  );

  const handleSetEndDate = useCallback(
    (date?: Date) => {
      if (!date) {
        return;
      }

      if (startDate && isBefore(date, startDate)) {
        setStartDate(date);
      }

      setEndDate(date);
      setEndDateOpen(false);
    },
    [startDate, setStartDate, setEndDate, setEndDateOpen],
  );

  return (
    <div className="w-full">
      <div className="flex mt-4">
        <Header title="Analytics" icon={LineChart} size="lg" separator>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Time Period</span>
            <div className="flex items-center gap-1">
              <DatePicker
                align="start"
                selected={startDate}
                onSelect={handleSetStartDate}
                onDismiss={setStartDateOpen}
                open={startDateOpen}
                fromDate={skuCreationDate}
                toDate={todayDate}
                disabled={isLoading}
              />
              <span className="text-sm font-medium">–</span>
              <DatePicker
                align="end"
                selected={endDate}
                onSelect={handleSetEndDate}
                onDismiss={setEndDateOpen}
                open={endDateOpen}
                fromDate={skuCreationDate}
                toDate={todayDate}
                disabled={isLoading}
              />
            </div>
          </div>
        </Header>
      </div>
      <div className="flex mt-8">
        <Header
          title="End-Consumer"
          description="Activity of consumers who have possession of tags"
        />
      </div>
      <div className="flex gap-4 mt-4">
        <Metric
          title="Page Views"
          icon={Eye}
          value={data?.tag_view_verifications_count}
        />
        <Metric
          title="Marketing Consent"
          icon={Mail}
          value={data?.email_consent_claim_count}
        />
        <Metric
          title="Ownership Claims"
          icon={SquareUserRound}
          value={data?.claim_count}
        />
        <Metric
          title="Ownership Resets"
          icon={RefreshCw}
          value={data?.reset_count}
        />
      </div>
      <div className="flex mt-4">
        <VerificationHeatMap data={data?.view_lat_long} />
      </div>
      <div className="flex mt-4">
        <ViewsChart data={data} />
      </div>
      <div className="flex mt-4">
        <DataGrid
          title="Ownership Activity"
          description="Last 10 ownership claims or resets made"
          adjustable
          data={data?.latest_tag_owner_changes}
        >
          <DataGrid.Column
            id="tag_id"
            name="Tag ID"
            emphasis
            extractor={getTagIDFromOwnerChange}
          />
          <DataGrid.Column
            id="serial"
            name="Serial Number"
            extractor={getTagSerialNumber}
          />
          <DataGrid.Column id="type" name="Type" extractor={getTransferType} />
          <DataGrid.Column
            id="email"
            name="Email"
            extractor={getConsumerEmail}
          />
          <DataGrid.Column
            id="user_id"
            name="User ID"
            ellipsis
            extractor={getConsumerUserId}
          />
          <DataGrid.Column
            id="time"
            name="Time"
            extractor={getTagVerificationTime}
          />
        </DataGrid>
      </div>
      <div className="flex gap-4 mt-4 items-stretch">
        <PageViewsChart
          data={data?.views_by_region}
          title="Regions by Page Views"
          description="Top 10 states, provinces, or regions with the most page views"
        />
        <PageViewsChart
          data={data?.views_by_country}
          title="Countries by Page Views"
          description="Top 10 countries or territories with the most page views"
        />
      </div>
      <div className="flex gap-4 mt-4 items-stretch">
        <DataGrid
          title="Cities by Page Views"
          data={data?.views_by_city}
          description="Top 10 cities with the most page views"
          adjustable
        >
          <DataGrid.Column
            id="city"
            name="City"
            emphasis
            extractor={getPageViewsByCityCity}
          />
          <DataGrid.Column
            id="region"
            name="Region"
            extractor={getPageViewsByCityRegion}
            ellipsis
          />
          <DataGrid.Column
            id="country"
            name="Country"
            extractor={getPageViewsByCityCountry}
            ellipsis
          />
          <DataGrid.Column id="views" name="Views" right />
        </DataGrid>
        <DataGrid
          title="Top Tags by Views"
          description="Top 10 tags with the most page views"
          data={data?.views_by_tag}
          adjustable
        >
          <DataGrid.Column
            id="tag_id"
            name="Tag ID"
            emphasis
            extractor={getTagsByViewsID}
          />
          <DataGrid.Column
            id="serial_number"
            name="Serial Number"
            extractor={getTagsByViewsSerialNumber}
          />
          <DataGrid.Column id="views" name="Views" right />
        </DataGrid>
      </div>
      <div className="flex mt-16">
        <Header
          title="Manufacturing and Supply Chain"
          description="Activity with tags throughout the process of programming and distribution"
        />
      </div>
      <div className="flex gap-4 mt-4">
        <Metric
          title="Total Verifications"
          icon={CircleCheck}
          value={data?.tag_verifications_count}
        />
        <Metric
          title="Programming Verifications"
          icon={Radio}
          value={data?.tag_program_verifications_count}
        />
        <Metric
          title="Custom Verifications"
          icon={CheckCheck}
          value={data?.tag_custom_verifications_count}
        />
        <Metric
          title="API Verifications"
          icon={Code}
          value={data?.external_tag_verifications_count}
        />
      </div>
      <div className="flex mt-4">
        <VerificationsChart data={data} />
      </div>
      <div className="flex mt-4">
        <DataGrid
          title="Recent Verifications"
          description="Last 10 tag verifications made"
          adjustable
          data={data?.latest_verifications}
        >
          <DataGrid.Column
            id="tag_id"
            name="Tag ID"
            emphasis
            extractor={getTagID}
          />
          <DataGrid.Column
            id="serial_number"
            name="Serial Number"
            extractor={getTagSerialNumber}
          />
          <DataGrid.Column
            id="location"
            name="Location"
            ellipsis
            fallback="Unknown"
            extractor={getTagVerificationLocation}
          />
          <DataGrid.Column
            id="type"
            name="Type"
            extractor={getTagVerificationType}
          />
          <DataGrid.Column
            id="time"
            name="Time"
            extractor={getTagVerificationTime}
          />
        </DataGrid>
      </div>
    </div>
  );
};
